import React from 'react';
import { graphql } from 'gatsby';
import Helmet from 'react-helmet';
import get from 'lodash/get';
import Img from 'gatsby-image';
import styled from 'styled-components';

import { Box, Flex, Text, Button } from '../components/neta';
import Link from '../components/Navigation/Link';
import theme from '../styles/theme';
import AuthorAttribution from '../components/Posts/AuthorAttribution';
import ShareSidebar from '../components/Posts/ShareSidebar';
import YouMightLike from '../components/Posts/YouMightLike';
import Rating from '../components/Posts/Rating';
import ContentContainer from '../layouts/ContentContainer';
import Disclosure from '../components/Posts/Disclosure';
import Layout from '../layouts';
import { colors } from '@styles/theme';
import { Email, Facebook, Twitter, LinkedIn } from '../components/SocialIcons';

export const pageQuery = graphql`
  query BlogPostBySlug($slug: String!) {
    contentfulBlogPost(slug: { eq: $slug }) {
      title
      publishDate(formatString: "MMMM Do, YYYY")
      description
      metaTitle
      metaDescription
      metaKeywords
      ctaText
      ctaLink
      heroAttributionName
      heroAttributionLink
      heroAltTag
      heroImage {
        sizes(maxWidth: 1180, background: "rgb:000000") {
          ...GatsbyContentfulSizes_withWebp
        }
      }
      body {
        childMarkdownRemark {
          html
        }
      }
      author {
        name
        shortBio {
          childMarkdownRemark {
            html
          }
        }
        image {
          sizes(maxWidth: 1180, background: "rgb:000000") {
            ...GatsbyContentfulSizes_withWebp
          }
        }
      }
      youMightLike {
        description
        heroImage {
          sizes(maxWidth: 1180, background: "rgb:000000") {
            ...GatsbyContentfulSizes_withWebp
          }
        }
        slug
        title
      }
      timeToRead
      twitterShareText
      facebookShareLink
      emailShareSubject
      emailShareBody
      linkedInShareLink
      slug
      canonicalUrl
      listicleItems {
        title
        image {
          sizes(maxWidth: 1180, background: "rgb:000000") {
            ...GatsbyContentfulSizes_withWebp
          }
        }
        imageLinkLocation
        imageCaption
        content {
          childMarkdownRemark {
            html
          }
        }
        enableShareLinks
        sharableMessage
      }
    }
  }
`;

const StyledImg = styled(Img)`
  height: 10em;
  background: #e1e1e1;
  font-size: 2em;
`;

const ListicleImg = styled(Img)`
  width: 60%;
`;

const ReadableContainer = styled(Box)`
  font-size: 1.2rem;
  line-height: 1.5;
  max-width: 50rem;
`;

const PostStyles = styled.div`
  font-size: 16px;
  line-height: 1.7;
  h1 {
    font-size: 2.25rem;
    line-height: 2.25rem;
    margin: 2rem 0;
  }

  h2 {
    font-size: 1.75rem;
    line-height: 1.75rem;
    margin: 2rem 0 1rem;
  }

  h3 {
    font-size: 1.5rem;
    line-height: 1.5rem;
    margin: 2rem 0;
  }

  h4 {
    font-size: 1.5rem;
    line-height: 1.5rem;
    margin: 1.5rem 0;
  }

  h5 {
    font-size: 1rem;
    line-height: 1rem;
  }

  h6 {
    font-size: 1rem;
    line-height: 1rem;
  }

  p {
    margin-bottom: 1rem;
    margin-top: 1rem;
    min-height: 1rem;
  }

  em {
    font-weight: 300;
  }

  a {
    color: ${colors.sharedDarker};
  }

  a:visited {
    color: ${colors.sharedDarker};
  }

  ul,
  ol {
    padding-left: 2rem;
    li {
      margin-bottom: 1rem;
    }
  }

  table thead tr {
    border-bottom: 1px solid ${colors.lightGray};
  }

  table thead tr th {
    text-align: left;
    font-size: 1rem;
    font-weight: 700;
    padding-bottom: 0.5rem;
  }

  table tbody tr td {
    padding-top: 3.5rem;
    padding-right: 2rem;
  }
`;

const renderListicleShareLinks = (slug, message, post) => {
  const postUrl = `https://www.askzeta.com/magazine/articles/${post.slug}#${slug}`;
  return (
    <Flex justifyContent="flex-end">
      <Box mr={3}>
        <Twitter shareText={`${message} ${postUrl}`} />
      </Box>
      <Box mr={3}>
        <Facebook shareUrl={postUrl} />
      </Box>
      <Box mr={3}>
        <Email contentItem={post} subject={message} body={postUrl} bg="#ffffff" />
      </Box>
      <Box>
        <LinkedIn shareUrl={postUrl} shareText={message || ''} />
      </Box>
    </Flex>
  );
};

const renderListicleItem = (listicleItem = {}, post) => {
  const {
    image,
    imageLinkLocation,
    imageCaption,
    title,
    content,
    enableShareLinks,
    sharableMessage
  } = listicleItem;
  const slug = (title || '').toLowerCase().replace(/ /g, '-');

  return (
    <Box mb={6} px={4} py={5} key={title}>
      <a name={slug} />
      <Flex flexDirection="column">
        <Box mb={2}>
          <Text.displayLarge>{title}</Text.displayLarge>
        </Box>
        {image ? (
          imageLinkLocation && imageLinkLocation !== '' ? (
            <a href={imageLinkLocation || '#'} target="_blank">
              <ListicleImg alt={title} sizes={image.sizes} />
            </a>
          ) : (
            <ListicleImg alt={title} sizes={image.sizes} />
          )
        ) : null}
        {(imageCaption || enableShareLinks) && (
          <Box mt={2} textAlign="right">
            {imageCaption && (
              <Box mb={2}>
                <Text.caption>{imageCaption}</Text.caption>
              </Box>
            )}
            {enableShareLinks && (
              <Box mb={4}>{renderListicleShareLinks(slug, sharableMessage, post)}</Box>
            )}
          </Box>
        )}
        {content && (
          <PostStyles
            dangerouslySetInnerHTML={{
              __html: content.childMarkdownRemark.html
            }}
          />
        )}
      </Flex>
    </Box>
  );
};

export default function BlogPostTemplate(props) {
  const post = get(props, 'data.contentfulBlogPost');
  const {
    author = { name: '' },
    canonicalUrl,
    ctaText,
    ctaLink,
    description,
    listicleItems,
    metaTitle,
    metaDescription,
    metaKeywords,
    slug,
    title,
    youMightLike
  } = post;

  return (
    <Layout>
      <ContentContainer>
        <Helmet>
          <title>{metaTitle?.length ? `${metaTitle} | Zeta` : `${title} | Zeta`}</title>
          <meta
            name="description"
            content={metaDescription?.length ? metaDescription : description}
          />
          <meta name="keywords" content={metaKeywords} />
          <meta property="og:title" content={metaTitle?.length ? metaTitle : title} />
          <meta
            property="og:description"
            content={metaDescription?.length ? metaDescription : description}
          />
          <meta property="og:image" content={`http:${post.heroImage.sizes.src}`} />
          <meta
            property="og:image:secure_url"
            content={`https:${post.heroImage.sizes.src}`}
          />
          <link
            rel="canonical"
            href={
              canonicalUrl
                ? canonicalUrl
                : `https://www.askzeta.com/magazine/articles/${slug}`
            }
          />
          <meta
            property="og:url"
            content={`https://www.askzeta.com/magazine/articles/${slug}`}
          />
        </Helmet>
        <Flex
          alignItems={['flex-start', 'center']}
          flexDirection={['column-reverse', 'row']}
          mt={[3, 5]}
          mb={5}
        >
          <Box mt={5} pr={5} width={[1, 1 / 2]}>
            <Box mb={5} textAlign={['center', 'left']}>
              <Text.displayLarge is="h1" fontWeight="bold">
                {post.title}
              </Text.displayLarge>
            </Box>
            <AuthorAttribution
              author={author}
              date={post.publishDate}
              timeToRead={post.timeToRead}
            />
          </Box>
          <Box width={[1, 1 / 2]}>
            <StyledImg
              alt={post.heroAltTag?.length ? post.heroAltTag : post.title}
              sizes={post.heroImage.sizes}
            />

            <Flex justifyContent="flex-end" mt={2}>
              {post.heroAttributionName && (
                <Link href={post.heroAttributionLink ? post.heroAttributionLink : '#'}>
                  <Text.caption color={theme.colors.primary}>
                    {post.heroAttributionName} |&nbsp;
                  </Text.caption>
                </Link>
              )}
              <Disclosure />
            </Flex>
          </Box>
        </Flex>
        <Flex justifyContent="space-between">
          <ShareSidebar post={post} />
          <ReadableContainer my={6}>
            <PostStyles
              dangerouslySetInnerHTML={{
                __html: post.body.childMarkdownRemark.html
              }}
            />
            {listicleItems &&
              listicleItems.map(item => {
                return renderListicleItem(item, post);
              })}
            {ctaLink && ctaText && (
              <Box textAlign="center">
                <Link href={ctaLink}>
                  <Button.form width="auto">{ctaText}</Button.form>
                </Link>
              </Box>
            )}
            <Rating />
            <YouMightLike posts={youMightLike || []} />
          </ReadableContainer>
        </Flex>
      </ContentContainer>
      {/* This is needed for modals to work */}
      <div id="portal-root" />
    </Layout>
  );
}
